import type { IViewModelMetadata } from '@featit/shared';

/** How long one read of a work area's model metadata answers every caller (I-1504). */
export const GENERIC_VIEW_METADATA_FRESH_MS = 5000;

export type MetadataReadsType = Map<string, { at: number; read: Promise<IViewModelMetadata> }>;

/** Every api's held reads (the signed-in one and each desktop connection's), so one forget reaches all. */
const everyMetadataReads = new Set<MetadataReadsType>();

export function createMetadataReads(): MetadataReadsType {
  const reads: MetadataReadsType = new Map();
  everyMetadataReads.add(reads);
  return reads;
}

/**
 * The work area's model changed — an item type, a part, a view-field write, the doorbell saying the
 * `item_types` or `parts` table moved: the next reader asks the server. Without an id, every work
 * area's (a system item type, or a write that does not name its work area). Every writer whose
 * result feeds `genericViewMetadata` calls this; `genericViewMetadataWriters.guard.test.ts` lists them.
 */
export function forgetGenericViewMetadata(projectId?: string): void {
  for (const reads of everyMetadataReads) {
    if (projectId === undefined) reads.clear();
    else reads.delete(projectId);
  }
  noteModelChanged(projectId);
}

/**
 * How long a burst of forgets settles before the pages that hold the model read it again (I-1533).
 * One write is a forget from the writer and, a moment later, the doorbell frame naming it; a retire
 * is three. Each page re-reads once for the burst, at most {@link MODEL_FOLLOW_MAX_WAIT_MS} after
 * its first forget.
 */
export const MODEL_FOLLOW_SETTLE_MS = 300;
export const MODEL_FOLLOW_MAX_WAIT_MS = 1500;

/** The work areas whose model changed in one burst, or `'all'` when a forget named none. */
export type ModelChangeType = ReadonlySet<string> | 'all';

const modelFollowers = new Set<(changed: ModelChangeType) => void>();
let pendingChange: Set<string> | 'all' | null = null;
let settleTimer: ReturnType<typeof setTimeout> | null = null;
let burstStartedAt = 0;

function noteModelChanged(projectId: string | undefined): void {
  if (modelFollowers.size === 0) return;
  if (pendingChange === null) {
    pendingChange = new Set();
    burstStartedAt = Date.now();
  }
  if (projectId === undefined) pendingChange = 'all';
  else if (pendingChange !== 'all') pendingChange.add(projectId);
  if (settleTimer) clearTimeout(settleTimer);
  const waited = Date.now() - burstStartedAt;
  settleTimer = setTimeout(
    flushModelChange,
    Math.max(0, Math.min(MODEL_FOLLOW_SETTLE_MS, MODEL_FOLLOW_MAX_WAIT_MS - waited)),
  );
}

function flushModelChange(): void {
  const changed = pendingChange;
  pendingChange = null;
  settleTimer = null;
  if (changed === null) return;
  for (const follower of [...modelFollowers]) follower(changed);
}

/**
 * The pages that hold what `genericViewMetadata` answers from — the Items page's item types, the
 * Parts page's tree, every has-parts flag and part picker — follow the one forget path: an in-tab
 * writer's forget and the doorbell's `item_types` / `parts` frame alike (I-1533, AC-12B55). Called
 * once per settled burst; returns the unfollow.
 */
export function followGenericViewMetadata(
  follower: (changed: ModelChangeType) => void,
): () => void {
  modelFollowers.add(follower);
  return () => {
    modelFollowers.delete(follower);
  };
}

/** Whether a settled burst touched this work area. */
export function modelChangeTouches(changed: ModelChangeType, workAreaId: string): boolean {
  return changed === 'all' || changed.has(workAreaId);
}

/**
 * A doorbell frame that may have changed the model: its item types, its parts (the active-part
 * count, AC-12B54), or a stretch the server missed.
 */
export function changesGenericViewMetadata(change: { source: string }): boolean {
  return (
    change.source === 'item_types' ||
    change.source === 'parts' ||
    change.source === 'listener_reconnected'
  );
}
